import { closeSnackbar, enqueueSnackbar } from 'notistack'
import errorMessages from 'utilities/errorMessages'
import i18next from '../../Services/localize/i18n'
import CloseIcon from '@mui/icons-material/Close'
import IconButton from '@mui/material/IconButton'
import { useState } from 'react'

const DismissButton = ({
    snackbarId,
    dismissText,
}: {
    snackbarId: string | number
    dismissText: string
}) => {
    const [hovered, setHovered] = useState(false)

    return (
        <span
            onClick={() => closeSnackbar(snackbarId)}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                border: '1px solid #fff',
                color: '#fff',
                padding: '4px 12px',
                borderRadius: '4px',
                cursor: 'pointer',
                userSelect: 'none',
                backgroundColor: hovered
                    ? 'rgba(255, 255, 255, 0.2)'
                    : 'transparent',
                transition: 'background-color 0.2s ease',
            }}
        >
            {dismissText}
        </span>
    )
}

const createAction = (persist?: boolean, dismissText?: string) => {
    if (!persist) return undefined

    return (snackbarId: string | number) =>
        dismissText ? (
            <DismissButton snackbarId={snackbarId} dismissText={dismissText} />
        ) : (
            <IconButton
                onClick={() => closeSnackbar(snackbarId)}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'common.white',
                }}
            >
                <CloseIcon fontSize="small" />
            </IconButton>
        )
}

const toast = {
    success: (
        message: string,
        additionValue?: any,
        duration?: number,
        persist?: boolean,
        dismissText?: string,
    ) => {
        const successMessage = i18next.t(message, additionValue)
        enqueueSnackbar(
            <span
                dangerouslySetInnerHTML={{ __html: successMessage }}
                style={{ userSelect: 'all', cursor: 'default' }}
            />,
            {
                variant: 'success',
                autoHideDuration: persist ? null : duration || 3000,
                persist: persist ?? false,
                action: createAction(persist, dismissText),
            },
        )
    },
    error: (
        message: string,
        additionValue?: any,
        duration?: number,
        persist?: boolean,
        dismissText?: string,
    ) => {
        const errorMessage = i18next.t(message, additionValue)
        enqueueSnackbar(
            <span
                dangerouslySetInnerHTML={{ __html: errorMessage }}
                style={{ userSelect: 'all', cursor: 'default' }}
            />,
            {
                variant: 'error',
                autoHideDuration: persist ? null : duration || 5000,
                persist: persist ?? false,
                action: createAction(persist, dismissText),
            },
        )
    },
    info: (
        message: string,
        additionValue?: any,
        duration?: number,
        persist?: boolean,
        dismissText?: string,
    ) => {
        const infoMessage = i18next.t(message, additionValue)
        enqueueSnackbar(
            <span
                dangerouslySetInnerHTML={{ __html: infoMessage }}
                style={{ userSelect: 'all', cursor: 'default' }}
            />,
            {
                variant: 'info',
                autoHideDuration: persist ? null : duration || 5000,
                persist: persist ?? false,
                action: createAction(persist, dismissText),
            },
        )
    },
    warning: (
        message: string,
        additionValue?: any,
        duration?: number,
        persist?: boolean,
        dismissText?: string,
    ) => {
        const warningMessage = i18next.t(message, additionValue)
        enqueueSnackbar(
            <span
                dangerouslySetInnerHTML={{ __html: warningMessage }}
                style={{ userSelect: 'all', cursor: 'default' }}
            />,
            {
                variant: 'warning',
                autoHideDuration: persist ? null : duration || 5000,
                persist: persist ?? false,
                action: createAction(persist, dismissText),
            },
        )
    },
    default: (
        message: string,
        additionValue?: any,
        duration?: number,
        persist?: boolean,
        dismissText?: string,
    ) => {
        const defaultMessage = i18next.t(message, additionValue)
        enqueueSnackbar(
            <span
                dangerouslySetInnerHTML={{ __html: defaultMessage }}
                style={{ userSelect: 'all', cursor: 'default' }}
            />,
            {
                variant: 'default',
                autoHideDuration: persist ? null : duration || 3000,
                persist: persist ?? false,
                action: createAction(persist, dismissText),
            },
        )
    },
}

export function showErrorToast(error: any) {
    let { message: errorMessageRes, arg } = errorMessages(error)
    toast.error(errorMessageRes, arg)
}

export enum ToastType {
    SUCCESS,
    ERROR,
    INFO,
    WARNING,
    DEFAULT,
}

export interface ToastProps {
    type?: ToastType
    message: string
    additionValue?: any
    duration?: number
    dismissText?: string
}

export function showPersistentToast(props: ToastProps) {
    switch (props.type) {
        case ToastType.SUCCESS:
            toast.success(
                props.message,
                props.additionValue,
                props.duration,
                true,
                props.dismissText,
            )
            break
        case ToastType.ERROR:
            toast.error(
                props.message,
                props.additionValue,
                props.duration,
                true,
                props.dismissText,
            )
            break
        case ToastType.INFO:
            toast.info(
                props.message,
                props.additionValue,
                props.duration,
                true,
                props.dismissText,
            )
            break
        case ToastType.WARNING:
            toast.warning(
                props.message,
                props.additionValue,
                props.duration,
                true,
                props.dismissText,
            )
            break
        default:
            toast.default(
                props.message,
                props.additionValue,
                props.duration,
                true,
                props.dismissText,
            )
            break
    }
}

export default toast
